/**
 * WithdrawApplicationPage - Public page for candidates to withdraw their application.
 *
 * Accessed via magic link: /withdraw/:withdrawToken
 * No login required — the token itself is the authentication.
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, XCircle, AlertTriangle, Loader2, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api, { apiErrorText } from '../../utils/api';

const WithdrawApplicationPage: React.FC = () => {
  const { withdrawToken } = useParams<{ withdrawToken: string }>();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [application, setApplication] = useState<any>(null);
  const [alreadyWithdrawn, setAlreadyWithdrawn] = useState(false);
  const [cannotWithdraw, setCannotWithdraw] = useState(false);
  const [withdrawn, setWithdrawn] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    fetchApplication();
  }, [withdrawToken]);

  const fetchApplication = async () => {
    try {
      const response = await api.get(`/api/applications/withdraw/${withdrawToken}`);
      const data = response.data;

      if (data.already_withdrawn) {
        setAlreadyWithdrawn(true);
        setApplication(data);
      } else if (data.cannot_withdraw) {
        setCannotWithdraw(true);
        setApplication(data);
      } else {
        setApplication(data);
      }
    } catch (err: any) {
      setError(apiErrorText(err?.response?.data?.detail) || t('withdraw.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    setSubmitting(true);
    setError('');
    try {
      await api.post(`/api/applications/withdraw/${withdrawToken}`, {
        reason: reason.trim() || null,
      });
      setWithdrawn(true);
    } catch (err: any) {
      setError(apiErrorText(err?.response?.data?.detail) || t('withdraw.errorWithdrawing'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-3">
          <Loader2 size={32} className="animate-spin text-indigo-500 mx-auto" />
          <p className="text-gray-600 text-sm">{t('withdraw.loading')}</p>
        </div>
      </div>
    );
  }

  if (error && !application) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full card text-center space-y-4">
          <XCircle size={48} className="mx-auto text-red-500" />
          <h1 className="text-xl font-bold text-gray-900">{t('withdraw.notFound')}</h1>
          <p className="text-gray-600 text-sm">{error}</p>
          <p className="text-gray-400 text-xs">{t('withdraw.invalidLink')}</p>
        </div>
      </div>
    );
  }

  // Already withdrawn
  if (alreadyWithdrawn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full card text-center space-y-4">
          <CheckCircle size={48} className="mx-auto text-green-500" />
          <h1 className="text-xl font-bold text-gray-900">{t('withdraw.alreadyWithdrawn')}</h1>
          <p className="text-gray-600 text-sm">
            {t('withdraw.alreadyWithdrawnDesc')}
          </p>
          {application?.position_title && (
            <p className="text-gray-500 text-sm">
              {application.position_title} — {application.company_name}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Cannot withdraw (hired or offer)
  if (cannotWithdraw) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full card text-center space-y-4">
          <AlertTriangle size={48} className="mx-auto text-amber-500" />
          <h1 className="text-xl font-bold text-gray-900">{t('withdraw.cannotWithdraw')}</h1>
          <p className="text-gray-600 text-sm">
            {t('withdraw.cannotWithdrawDesc')}
          </p>
          {application?.position_title && (
            <p className="text-gray-500 text-sm">
              {application.position_title} — {application.company_name}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Successfully withdrawn
  if (withdrawn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full card text-center space-y-4">
          <CheckCircle size={48} className="mx-auto text-green-500" />
          <h1 className="text-xl font-bold text-gray-900">{t('withdraw.success')}</h1>
          <p className="text-gray-600 text-sm">
            {t('withdraw.successDesc')}
          </p>
          {application?.position_title && (
            <p className="text-gray-500 text-sm">
              {application.position_title} — {application.company_name}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Confirmation form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md w-full card space-y-6">
        <div className="text-center space-y-2">
          <AlertTriangle size={40} className="mx-auto text-amber-500" />
          <h1 className="text-xl font-bold text-gray-900">{t('withdraw.title')}</h1>
          <p className="text-gray-600 text-sm">
            {t('withdraw.subtitle')}
          </p>
        </div>

        {/* Application details */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">{t('withdraw.position')}</span>
            <span className="font-medium text-gray-900">{application?.position_title}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">{t('withdraw.company')}</span>
            <span className="font-medium text-gray-900">{application?.company_name}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">{t('withdraw.candidate')}</span>
            <span className="font-medium text-gray-900">{application?.candidate_name}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">{t('withdraw.appliedDate')}</span>
            <span className="font-medium text-gray-900">
              {application?.applied_at ? new Date(application.applied_at).toLocaleDateString() : '-'}
            </span>
          </div>
        </div>

        {/* Warning */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          {t('withdraw.warning')}
        </div>

        {/* Reason (optional) */}
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">
            {t('withdraw.reasonLabel')}
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="input text-sm"
            rows={3}
            placeholder={t('withdraw.reasonPlaceholder')}
            disabled={submitting}
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={handleWithdraw}
            disabled={submitting}
            className="flex-1 bg-red-500 text-white font-medium py-2.5 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                {t('withdraw.processing')}
              </>
            ) : (
              t('withdraw.confirm')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WithdrawApplicationPage;
